/* Actual front-end for Drop */

import QueueUpload from "./queue-upload.js";
import DropAPI from "./drop-api.js";
import { BackBlazeB2FileStore } from "./file-store.js";
import { Base64Config } from "./config-store.js";
import Secret from "./secret.js";
import DOMLocalizer from "./dom-localizer.js";
import getSizeString from "./get-size-string.js";
import WebPushController from "./web-push-controller.js";
import PopImageController from "./pop-image-controller.js";
import ListController from "./list-controller.js";
import ChangeFileUI from "./change-file-ui.js";
import { uriFromKey, keyFromComponents, splitKey } from "./paths.js";
import FileInputController from "./file-input-controller.js";
import UploadController from "./upload-controller.js";

export default class DropApp {
  constructor() {
    const webPushController = new WebPushController();
    webPushController.onCheckingWebPushAvailability = async () => {
      return api.passthroughFileStore.getFile(WebPushController.VAPID_KEY_PATH);
    };
    webPushController.onGettingPushSubscriptionDetail =
      /**
       * @param {string} path
       */
      async (path) => api.passthroughFileStore.getFile(path, "json");

    webPushController.onAddingPushSubscriptionDetail =
      /**
       * @param {string} path
       * @param {import("./web-push-controller.js").ServerPushSubscriptionJSON} serverJson
       */
      async (path, serverJson) => {
        const [result] = await api.passthroughFileStore.setFile(
          path,
          serverJson,
          "json",
        );
        return result;
      };
    webPushController.onRemovingPushSubscriptionDetail =
      /**
       * @param {string} path
       */
      async (path) => {
        const [result] = await api.passthroughFileStore.deleteFile(path);
        return result;
      };

    const queueUpload = new QueueUpload();

    const api = new DropAPI(Base64Config, BackBlazeB2FileStore);
    api.onConfigReady = async function configReady() {
      _paq.push(["trackEvent", "DropAPI", "ConfigReady"]);

      listController.handleCurrentUrlHash();
      webPushController.loggedIn();
      popImageController.loggedIn();
    };
    api.onConfigError =
      /**
       * @param {boolean} hasAccess
       */
      function gotConfig(hasAccess) {
        _paq.push(["trackEvent", "DropAPI", "ConfigError"]);

        alert(localizer.get("incorrectUsernamePassword"));
        secret.logout();
      };

    const secret = new Secret();
    secret.usernameElement = /** @type {HTMLInputElement} */ (
      document.getElementById("secret_username")
    );
    secret.passwordElement = /** @type {HTMLInputElement} */ (
      document.getElementById("secret_password")
    );

    const localizer = new DOMLocalizer();
    localizer.observe(document.documentElement);
    DOMLocalizer.sharedLocalizer = localizer;

    /** @type {Date} */
    const downloadLinkExpireDate = new Date(
      new Date().getTime() + 60 * 60 * 24 * 2 * 1000,
    );

    /** @type {WeakMap<File, string>} Map b/t file to upload and target folder. */
    const fileToListPrefixMap = new WeakMap();
    const fileContainerElement = /** @type {HTMLParagraphElement} */ (
      document.getElementById("file-container")
    );

    // Textual label status
    const statusElement = /** @type {HTMLParagraphElement} */ (
      document.getElementById("status")
    );

    /**
     * @param {string} [name]
     * @param {number} [loaded]
     * @param {number} [total]
     * @param {boolean} [isRetry]
     */
    function updateStatus(name, loaded, total, isRetry) {
      if (!isUploading) {
        document.body.classList.remove("uploading");
        statusElement.dataset.l10nKey = "done";
      } else {
        document.body.classList.add("uploading");
        if (
          name == undefined ||
          loaded === undefined ||
          total === undefined ||
          isRetry === undefined
        ) {
          throw new Error();
        }

        if (!DOMLocalizer.sharedLocalizer) {
          throw new Error();
        }
        const num = queueUpload.getQueueLength();
        statusElement.dataset.l10nKey =
          DOMLocalizer.sharedLocalizer.appendPluralRules(
            encryptingFile
              ? "preparingUploadStat"
              : isRetry
                ? "retryUploadStat"
                : "uploadStat",
            num,
          );
        statusElement.dataset.l10nSubstitutions = JSON.stringify({
          name,
          loaded: getSizeString(loaded, true),
          total: getSizeString(total, true),
          percent: total === 0 ? "0" : ((loaded / total) * 100).toFixed(2),
          num: num.toString(),
        });
      }
    }
    if (!queueUpload.isSupported || !window.JSON) {
      statusElement.dataset.l10nKey = "unsupported";
    }

    /** @type {HTMLFormElement} */ (
      document.getElementById("login-form")
    ).addEventListener("submit", (event) => {
      event.preventDefault();
      secret.login();
    });

    const changeFileUI = new ChangeFileUI({
      dialogElement: /** @type {HTMLDialogElement} */ (
        document.getElementById("change-file-dialog")
      ),
      listFiles(prefix) {
        return api.fileStore.listFiles(prefix);
      },
      getPendingFiles() {
        const files = queueUpload.getPendingFiles();
        return files.map((file) => {
          const prefix = fileToListPrefixMap.get(file);
          if (prefix === undefined) {
            throw new Error();
          }
          return [file, prefix];
        });
      },
      getCurrentFile() {
        return queueUpload.getCurrentFile();
      },
    });

    let showHiddenFiles = false;
    let showPlainTextListing = false;

    const listStatusHiddenElement = /** @type {HTMLSpanElement} */ (
      document.getElementById("list-status-hidden")
    );
    const listStatusLockedElement = /** @type {HTMLSpanElement} */ (
      document.getElementById("list-status-locked")
    );

    let lastKeypressCode = "";
    /** @type {ReturnType<setTimeout>|undefined} */
    let keypressTimer = undefined;

    document.body.addEventListener("keypress", (event) => {
      if (
        event.target instanceof HTMLInputElement ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      ) {
        clearTimeout(keypressTimer);
        return;
      }

      if (lastKeypressCode === "") {
        lastKeypressCode = event.code;
        event.preventDefault();
        keypressTimer = setTimeout(() => {
          lastKeypressCode = "";
        }, 500);
        return;
      }

      if (lastKeypressCode !== event.code) {
        clearTimeout(keypressTimer);
        lastKeypressCode = "";
        event.preventDefault();
        return;
      }

      clearTimeout(keypressTimer);
      lastKeypressCode = "";
      event.preventDefault();

      switch (event.code) {
        case "KeyL":
          togglePlaintextListing();
          event.preventDefault();
          break;
        case "KeyH":
          toggleHiddenFiles();
          event.preventDefault();
          break;
        case "KeyX":
          toggleExperiments();
          event.preventDefault();
          break;
      }
    });

    document.body.addEventListener("keypress", (event) => {
      if (event.target instanceof HTMLInputElement) {
        return;
      }

      switch (event.code) {
        case "KeyT":
          listController.openGoToForm();
          event.preventDefault();
          break;
      }
    });

    const togglePlaintextListing = () => {
      if (!api.encryptionAvailable) {
        return;
      }

      showPlainTextListing = !showPlainTextListing;
      _paq.push([
        "trackEvent",
        "UI",
        "TogglePlaintextListing",
        showPlainTextListing.toString(),
      ]);

      listStatusLockedElement.hidden = !showPlainTextListing;
      api.setEnabledEncryption(!showPlainTextListing);
      listController.reloadFileList();
    };

    const toggleHiddenFiles = () => {
      showHiddenFiles = !showHiddenFiles;
      _paq.push([
        "trackEvent",
        "UI",
        "ToggleShowHiddenFiles",
        showHiddenFiles.toString(),
      ]);

      listStatusHiddenElement.hidden = !showHiddenFiles;
      listController.reloadFileList();
    };

    const toggleExperiments = () => {
      _paq.push([
        "trackEvent",
        "UI",
        "ToggleExperiments",
        (!document.documentElement.classList.contains(
          "experimental-features",
        )).toString(),
      ]);

      document.documentElement.classList.toggle("experimental-features");
    };

    const listController = new ListController({
      listElement: /** @type {HTMLUListElement} */ (
        document.getElementById("item-list")
      ),
      listStatusElement: /** @type {HTMLSpanElement} */ (
        document.getElementById("list-status")
      ),
      breadcrumbElement: /** @type {HTMLUListElement} */ (
        document.getElementById("breadcrumb")
      ),
      /**
       * @param {string} key
       */
      async showDetailFile(key) {
        const [result] = await api.fileStore.statFile(uriFromKey(key));

        if (!result) {
          return [!!result];
        }

        const { filename, humanizedLocation } = splitKey(key);
        const {
          filename: storedFilename,
          humanizedLocation: storedHumanizedLocation,
        } = api.fileEncryption
          ? splitKey(await api.encryptName(key))
          : { filename, humanizedLocation };
        const {
          contentType,
          size,
          storedSize,
          uploaded,
          eTag,
          modified,
          created,
        } = result;

        alert(
          localizer.get("fileDetailMessage", {
            filename,
            storedFilename,
            path: humanizedLocation,
            storedPath: storedHumanizedLocation,
            contentType: contentType ?? "-",
            size: size !== undefined ? getSizeString(size) : "-",
            storedSize:
              storedSize !== undefined ? getSizeString(storedSize) : "-",
            uploaded: uploaded
              ? new Intl.DateTimeFormat(localizer.locale, {
                  dateStyle: "short",
                  timeStyle: "short",
                }).format(uploaded)
              : "-",
            eTag: eTag ?? "-",
            modified: modified
              ? new Intl.DateTimeFormat(localizer.locale, {
                  dateStyle: "short",
                  timeStyle: "short",
                }).format(modified)
              : "-",
            created: created
              ? new Intl.DateTimeFormat(localizer.locale, {
                  dateStyle: "short",
                  timeStyle: "short",
                }).format(created)
              : "-",
            encryptedState: api.fileEncryption
              ? localizer.get("encryptedStateYes")
              : localizer.get("encryptedStateNo"),
          }),
        );
        return [!!result];
      },
      /**
       * @param {string} key
       */
      async deleteFile(key) {
        const [result, errorInfo] = await api.fileStore.deleteFile(
          uriFromKey(key),
        );
        if (result) {
          _paq.push(["trackEvent", "DropAPI", "DeleteFileSuccess", key]);
        } else {
          const name = splitKey(key).filename;
          if (errorInfo) {
            alert(
              localizer.get("fileDeleteError", { name }) +
                "\n" +
                localizer.get("errorWithInfo", {
                  code: errorInfo.code,
                  message: errorInfo.message,
                }),
            );
          } else {
            alert(localizer.get("fileDeleteError", { name }));
          }
          _paq.push(["trackEvent", "DropAPI", "DeleteFileError", key]);
        }
        return [result];
      },
      async getFile(key, delegate) {
        if (!api.fileEncryption) {
          const result = await api.fileStore.getFile(
            uriFromKey(key),
            "file",
            delegate,
          );
          if (result) {
            _paq.push(["trackEvent", "DropAPI", "GetFileSuccess", key]);
          } else {
            const name = splitKey(key).filename;
            alert(localizer.get("fileGetError", { name }));
            _paq.push(["trackEvent", "DropAPI", "GetFileError", key]);
            return [null];
          }

          return [result];
        }

        const result = await api.fileStore.getFile(
          uriFromKey(key),
          "stream",
          delegate,
        );
        if (result) {
          _paq.push(["trackEvent", "DropAPI", "GetFileSuccess", key]);
        } else {
          const name = splitKey(key).filename;
          alert(localizer.get("fileGetError", { name }));
          _paq.push(["trackEvent", "DropAPI", "GetFileError", key]);
          return [null];
        }

        const { type, lastModified, stream: sourceStream } = result;

        const stream = await api.decryptFile(sourceStream);
        if (!stream) {
          return [null];
        }
        const reader = stream.getReader();
        /** @type {Uint8Array<ArrayBuffer>[]} */
        const blobParts = [];
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }
          const array = new Uint8Array(value.byteLength);
          array.set(value);
          blobParts.push(array);
        }

        return [
          new File(blobParts, splitKey(key).filename, {
            type,
            lastModified,
          }),
        ];
      },
      async changeFile(key) {
        /** @type {string|undefined} */
        let newKey = undefined;
        while (true) {
          newKey = await changeFileUI.open(key, newKey);

          if (!newKey) {
            changeFileUI.close();
            return [false];
          }

          const [result, errorInfo] = await api.fileStore.moveFile(
            uriFromKey(key),
            uriFromKey(newKey),
          );

          if (!result) {
            const name = splitKey(key).filename;
            if (errorInfo) {
              alert(
                localizer.get("fileChangeError", { name }) +
                  "\n" +
                  localizer.get("errorWithInfo", {
                    code: errorInfo.code,
                    message: errorInfo.message,
                  }),
              );
            } else {
              alert(localizer.get("fileChangeError", { name }));
            }
            _paq.push(["trackEvent", "DropAPI", "ChangeFileError", key]);

            continue;
          }

          const newPrefix = splitKey(newKey).prefix;
          _paq.push(["trackEvent", "DropAPI", "ChangeFileSuccess", key]);
          changeFileUI.close();

          if (newPrefix.includes(listController.listPrefix)) {
            listController.reloadFileList();
          }
          return [result];
        }
      },
      /**
       * @param {string} prefix
       */
      async listFiles(prefix) {
        const [results, errorInfo] = await api.fileStore.listFiles(prefix);
        if (!results && errorInfo?.code === "RequestTimeTooSkewed") {
          // Try again since the awsTimeOffset should have been updated now.
          return this.listFiles(prefix);
        }

        _paq.push(["trackEvent", "DropAPI", "ListFiles", prefix]);
        return [results, errorInfo];
      },
      /**
       * @param {string} key
       * @param {File} file
       */
      async cancelFile(key, file) {
        queueUpload.cancelFile(file);
        _paq.push(["trackEvent", "UI", "CancelFile", key]);
      },
      async getStoredSize(size) {
        return api.fileEncryption ? await api.encryptedFileSize(size) : size;
      },
      /**
       * @param {string} hash
       */
      pushState(hash) {
        window.history.pushState(null, "", hash);
      },
      getPendingUploadFiles() {
        const files = queueUpload.getPendingFiles();
        return files.map((file) => {
          const prefix = fileToListPrefixMap.get(file);
          if (prefix === undefined) {
            throw new Error();
          }
          return [file, prefix];
        });
      },
      getCurrentUploadingFile() {
        return queueUpload.getCurrentFile();
      },
      async getDownloadUrl(key) {
        if (api.fileEncryption) {
          return "";
        }

        return api.fileStore.getDownloadUrl(
          uriFromKey(key),
          downloadLinkExpireDate,
        );
      },
      toggleHiddenFiles,
      toggleExperiments,
      togglePlaintextListing,
      get showHiddenFiles() {
        return showHiddenFiles;
      },
    });

    this.fileInputController = new FileInputController({
      fileContainerElement,
      fileInputElement: /** @type {HTMLInputElement} */ (
        document.getElementById("files")
      ),
      secret,
      api,
      listController,
      queueUpload,
      fileToListPrefixMap,
      localizer,
    });

    window.addEventListener("popstate", () =>
      listController.handleCurrentUrlHash(),
    );

    const popImageController = new PopImageController({
      container: document.body,
      async getImages() {
        const [results] = await api.fileStore.listFiles(
          PopImageController.IMAGES_PREFIX,
        );
        if (!results) {
          return [];
        }

        return (
          await Promise.all(
            results.map(async (result) => {
              return await api.fileStore.getFile(
                uriFromKey(result.key),
                "file",
              );
            }),
          )
        ).filter((file) => file instanceof File);
      },
    });

    /**
     * The uploading status to be reflected on UI.
     * This is different from `QueueUpload##isUploading`
     * because we may deviate when API calls failed.
     */
    let isUploading = false;

    /** @type {File|null} */
    let encryptingFile = null;

    this.uploadController = new UploadController({
      queueUpload,
      api,
      listController,
      fileToListPrefixMap,
      downloadLinkExpireDate,
      updateStatus,
      localizer,
      state: {
        get encryptingFile() {
          return encryptingFile;
        },
        set encryptingFile(value) {
          encryptingFile = value;
        },
        get isUploading() {
          return isUploading;
        },
        set isUploading(value) {
          isUploading = value;
        },
      },
    });

    /**
     * @param {string} name
     */
    queueUpload.onMaxFileSizeExceed = (name) => {
      _paq.push(["trackEvent", "QueueUpload", "MaxFileSizeExceed", name]);

      alert(
        localizer.get("fileSizeExceedError", {
          name,
        }),
      );
    };

    /**
     *  @param {{ username: string; password: string; }} secret
     */
    secret.onLogin = function (secret) {
      _paq.push(["trackEvent", "Secret", "Login", secret.username]);

      api.configStore.SECRET = secret;
      api.getConfig();

      document.body.classList.remove("auth-needed");
      statusElement.dataset.l10nKey = "statusInstruction";
    };
    secret.onLogout = function loggedOut() {
      _paq.push([
        "trackEvent",
        "Secret",
        "Logout",
        api.configStore.SECRET?.username,
      ]);

      api.configStore.SECRET = null;
      api.clearConfig();

      document.body.classList.add("auth-needed");
      delete statusElement.dataset.l10nKey;
      statusElement.textContent = "";

      webPushController.loggedOut();
      listController.loggedOut();
    };

    // Start the transition
    document.body.classList.remove("uninit");

    // There is no pseudo-class like :from() to target for
    // the animation when leaving 'uninit' state.
    // We will have to introduce a new state here.
    document.body.classList.add("leave-uninit");
    // We should be using animationend & webkitAnimationEnd here, however
    // the event will never be triggered if the animation is interrupted.
    setTimeout(function animationend() {
      document.body.classList.remove("leave-uninit");
      secret.usernameElement?.focus();
    }, 1010);

    document.body.classList.add("auth-needed");
  }
}
